import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Project } from '@/features/projects/api/projects.schemas';
import { useSelectedOrganizationDetails } from '@/features/organizations/hooks';
import type { Organization } from '@/features/organizations/api/organization.schemas';

/**
 * ProjectSelector Component
 *
 * Dropdown selector for switching between projects in the current organization.
 *
 * Responsibilities:
 * - Display available projects
 * - Handle project selection
 * - Show current project
 * - Display loading state during fetch
 */

interface ProjectSelectorProps {
  projects: Project[];
  selectedProject: Project | null;
  onProjectChange: (project: Project) => void;
  loading?: boolean;
}

/**
 * Resolves the role the current user holds on `projectId` within
 * `selectedOrganization`. Logic mirrors the authorizer + handler
 * scoping:
 *   - org admin/owner → always uses the org-role.
 *   - explicit `{id: "*"}` wildcard OR empty `projects[]` for
 *     dev/auditor → org-role applies to every project.
 *   - scoped → per-project role from the matching entry, or null
 *     if the project isn't in the user's assignments (BFF would
 *     have filtered it out, but guard anyway).
 */
function resolveProjectRole(
  selectedOrganization: Organization | null,
  projectId: string,
): string | null {
  if (!selectedOrganization) return null;
  const role = selectedOrganization.role;
  if (role === 'administrator' || role === 'owner') return role;
  const assignments = (selectedOrganization.metadata?.projects ?? []) as Array<{
    id: string;
    role?: string;
  }>;
  const hasWildcard = assignments.some((p) => p.id === '*');
  const isAllMode =
    hasWildcard ||
    (assignments.length === 0 && (role === 'developer' || role === 'auditor'));
  if (isAllMode) return role;
  const match = assignments.find((p) => p.id === projectId);
  return match?.role ?? null;
}

export function ProjectSelector({
  projects,
  selectedProject,
  onProjectChange,
  loading = false,
}: ProjectSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOrganization = useSelectedOrganizationDetails();

  // If no projects and not loading, show empty state
  if (projects.length === 0 && !loading) {
    return (
      <span className="text-sm text-muted-foreground">No projects available</span>
    );
  }

  // If loading, show loading state
  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  const handleValueChange = (value: string) => {
    const project = projects.find(proj => proj.id === value);
    if (project) {
      onProjectChange(project);
    }
    setIsOpen(false);
  };



  return (
    <Select
      value={selectedProject?.id || ''}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={loading}
    >
      <SelectTrigger className="w-[180px] sm:w-[280px] lg:w-[400px] justify-between">
        {selectedProject ? (
          <div className="flex items-center justify-between w-full min-w-0 gap-2">
            <span className="truncate text-left flex-1">{selectedProject.name}</span>
            <ProjectMeta
              status={selectedProject.status}
              role={resolveProjectRole(selectedOrganization, selectedProject.id)}
              className="hidden sm:flex"
            />
          </div>
        ) : (
          <SelectValue placeholder="Select project" />
        )}
      </SelectTrigger>
      <SelectContent align="end" className="w-[180px] sm:w-[280px] lg:w-[400px]">
        {projects.map((project) => (
          <SelectItem key={project.id} value={project.id} className="flex-col items-start">
            <div className="flex items-center justify-between w-full min-w-0 gap-2">
              <span className="font-medium truncate flex-1">{project.name}</span>
              <ProjectMeta
                status={project.status}
                role={resolveProjectRole(selectedOrganization, project.id)}
              />
            </div>
            {project.description && (
              <span className="text-xs text-muted-foreground truncate w-full mt-1">
                {project.description}
              </span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Right-aligned project metadata: status pill + user role on this
 * project, separated by a middle-dot. Hidden on the smallest screens
 * (caller passes `hidden sm:flex` for the trigger to keep it compact).
 */
function ProjectMeta({
  status,
  role,
  className,
}: {
  status: string;
  role: string | null;
  className?: string;
}) {
  return (
    <span
      className={`text-[10px] sm:text-[11px] text-muted-foreground flex-shrink-0 flex items-center gap-1.5 ${className ?? ''}`}
    >
      <span>{status}</span>
      {role && (
        <>
          <span aria-hidden="true">·</span>
          <span>{role}</span>
        </>
      )}
    </span>
  );
}